import React from "react";
import Icon from "@components/UI/Icon";
import Tooltip from "@components/UI/Tooltip";
import { PRICE_PLANS } from "../plansData";

type PlanKey = (typeof PRICE_PLANS)[number]["key"]

type FeatureByPlan = Record<PlanKey, FeaturePresenceDescription>

type FeaturePresenceDescription = CustomPresence | Present | NotPresent

type CustomPresence = {
  type: "custom",
  content: React.ReactNode
}

type Present = {
  type: "present",
}

type NotPresent = {
  type: "notPresent"
}

type Feature = {
  title: React.ReactNode;
  description?: React.ReactNode;
  byPlan: FeatureByPlan;
}

type FeatureScope = {
  scopeTitle?: React.ReactNode;
  features: Feature[];
}

const PRESENT: Present = {
  type: "present",
}

const NOT_PRESENT: NotPresent = {
  type: "notPresent",
}

const BASIC_FEATURES = [
  {
    title: "Seats",
    byPlan: {
      free: {
        type: "custom",
        content: "Up to 3",
      },
      plus: {
        type: "custom",
        content: "Up to 10",
      },
      business: {
        type: "custom",
        content: "Up to 50",
      },
      scale: {
        type: "custom",
        content: "Up to 300",
      },
      enterprise: {
        type: "custom",
        content: "300+",
      },
    },
  },
  {
    title: "Support",
    byPlan: {
      free: NOT_PRESENT,
      plus: {
        type: "custom",
        content: "Email support",
      },
      business: {
        type: "custom",
        content: "Email support",
      },
      scale: {
        type: "custom",
        content: "Email support",
      },
      enterprise: {
        type: "custom",
        content: "Customer Success Agent",
      },
    },
  },
] satisfies Feature[]

const TIME_TRACKING_FEATURES = [
  {
    title: "Web, desktop & browser",
    description: "Track time from the web app, desktop apps for Windows and Mac, or browser extension.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Automatic timer",
    description: "Start and stop a timer to record time automatically.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Manual time entries",
    description: "Add or edit time entries manually.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Tasks",
    description: "Add task names to time entries and group identical entries automatically.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Billable time",
    description: "Mark time as billable or non-billable.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Timesheets",
    description: "Tracked time is automatically grouped into ready-to-submit timesheets.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Offline tracking",
    description: "Continue tracking time without an internet connection and sync later.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Time logs",
    description: "View grouped time entries with full details including task, project, billable status, duration, and time range.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Integrations",
    description: "20+ integrations available with the Traqq browser extension.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Break tracking",
    description: "Track break time separately from working time.",
    byPlan: {
      free: NOT_PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
] satisfies Feature[]

const PROJECTS_AND_CLIENTS_FEATURES = [
  {
    title: "Projects",
    description: "Create and organize projects with custom names and colors. Track time per project for reporting and billing.",
    byPlan: {
      free: {
        type: "custom",
        content: "Up to 1",
      },
      plus: {
        type: "custom",
        content: "Unlimited",
      },
      business: {
        type: "custom",
        content: "Unlimited",
      },
      scale: {
        type: "custom",
        content: "Unlimited",
      },
      enterprise: {
        type: "custom",
        content: "Unlimited",
      },
    },
  },
  {
    title: "Project pay rates",
    description: "Set billable and cost rates for individual projects.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Tags",
    description: "Categorize time entries with custom tags for additional context.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Clients",
    description: "Store client information and link clients to projects.",
    byPlan: {
      free: NOT_PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Multiple currencies",
    description: "Set a currency for each client to use across financial reports, projects, and expenses.",
    byPlan: {
      free: NOT_PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Project managers",
    description: "Assign project managers to oversee individual projects.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Project forecasting",
    description: "View project progress against planned hours or budget and see burn rate, forecasts, status, and key insights.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  }
] satisfies Feature[]

const BILLING_AND_FINANCE_FEATURES = [
  {
    title: "Billable & cost rates",
    description: "Set billable and cost rates for your workspace, team members, and projects.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Project financial reporting",
    description: "View project financial data in the dashboard, reports, and project profiles.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Expenses",
    description: "Track and approve work-related expenses.",
    byPlan: {
      free: NOT_PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Client invoices",
    description: "Generate invoices from tracked time and send them to clients.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Uninvoiced time",
    description: "See tracked time not yet included in any invoice, along with the uninvoiced amount.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  }
] satisfies Feature[]

const TEAM_MANAGEMENT_FEATURES = [
  {
    title: "Teams",
    description: "Organize users into teams.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Individual pay rates",
    description: "Set billable and cost rates for each team member.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Schedules & capacity",
    description: "Define individual work schedules and capacity.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Time off calendar",
    description: "Add your time off and view team availability in a shared calendar.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Role-based access",
    description: "Set workspace roles and assign team and project managers to control access to reports, financial data, and workspace settings.",
    byPlan: {
      free: {
        type: "custom",
        content: (
          <Tooltip title="Only the Owner can manage the workspace. No Admin roles or project/team managers are available.">
            <Icon
              name="info-circle-outlined"
              color="#6A6FD7"
              size={16}
            />
          </Tooltip>
        ),
      },
      plus: {
        type: "custom",
        content: (
          <Tooltip title="Only the Owner can manage the workspace. No Admin roles or project/team managers are available.">
            <Icon
              name="info-circle-outlined"
              color="#6A6FD7"
              size={16}
            />
          </Tooltip>
        ),
      },
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Workload analysis",
    description: "See who's under, around, or over capacity based on planned capacity and tracked time.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Overtime tracking",
    description: "View overtime hours separately.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Focus & distractions",
    description: "See how focused your team is, with metrics on deep work, task switching, and focus trends.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: NOT_PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Burnout radar",
    description: "Identify team members at risk of burnout and see the factors driving that risk.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: NOT_PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  }
] satisfies Feature[]

const REPORTS_AND_ANALYTICS_FEATURES = [
  {
    title: "Time & financial dashboards",
    description: "View dashboards for tracked time and financial metrics, including billable vs. non-billable time, revenue, costs, and margins.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Detailed time records",
    description: "Review every tracked time entry with its full details.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Time by team & user",
    description: "Compare tracked time across teams and individual users.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Time by project",
    description: "Analyze tracked time by project.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Time by client",
    description: "View tracked time grouped by client.",
    byPlan: {
      free: NOT_PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Compliance reports",
    description: "Review detailed attendance and compliance records.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Capacity reporting",
    description: "Compare planned capacity with actual tracked time.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Work & break reports",
    description: "Compare work time and break time across your team.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Attendance & absences",
    description: "View clock-in, clock-out, and absence records for every team member.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
] satisfies Feature[]

const ETHICAL_TIME_TRACKING_FEATURES = [
  {
    title: "No screenshot monitoring",
    description: "Traqq never takes screenshots of users' screens.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "No keystroke tracking",
    description: "Traqq never records keystrokes or typed content.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "No activity monitoring",
    description: "Traqq does not calculate productivity using activity percentages.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "No idle time tracking",
    description: "Traqq does not monitor keyboard or mouse inactivity.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "No app or website tracking",
    description: "Traqq does not record which apps or websites your team uses.",
    byPlan: {
      free: PRESENT,
      plus: PRESENT,
      business: PRESENT,
      scale: PRESENT,
      enterprise: PRESENT,
    },
  },
] satisfies Feature[]

const ENTERPRISE_CONTROLS_FEATURES = [
  {
    title: "Isolated infrastructure",
    description: "Dedicated virtual server for your organization.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: NOT_PRESENT,
      scale: NOT_PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Custom integrations on request",
    description: "Connect Traqq to your internal tools and workflows.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: NOT_PRESENT,
      scale: NOT_PRESENT,
      enterprise: PRESENT,
    },
  },
  {
    title: "Audit logs",
    description: "Keep a record of important workspace activity.",
    byPlan: {
      free: NOT_PRESENT,
      plus: NOT_PRESENT,
      business: NOT_PRESENT,
      scale: NOT_PRESENT,
      enterprise: PRESENT,
    },
  },
] satisfies Feature[]

export const FEATURES: FeatureScope[] = [
  {
    features: BASIC_FEATURES,
  },
  {
    scopeTitle: "Time tracking",
    features: TIME_TRACKING_FEATURES,
  },
  {
    scopeTitle: "Projects & clients",
    features: PROJECTS_AND_CLIENTS_FEATURES,
  },
  {
    scopeTitle: "Billing & finance",
    features: BILLING_AND_FINANCE_FEATURES,
  },
  {
    scopeTitle: "Team management",
    features: TEAM_MANAGEMENT_FEATURES,
  },
  {
    scopeTitle: "Reports & analytics",
    features: REPORTS_AND_ANALYTICS_FEATURES,
  },
  {
    scopeTitle: "Ethical time tracking",
    features: ETHICAL_TIME_TRACKING_FEATURES,
  },
  {
    scopeTitle: "Enterprise controls",
    features: ENTERPRISE_CONTROLS_FEATURES,
  },
]
